"use client";

import {
  Box,
  Button,
  Icon,
  ModalContent,
  ModalHeader,
  PortalModal,
} from "@cloai/uikit";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { FC, SyntheticEvent, useCallback } from "react";

import { LanguageCode, useFlag } from "@/components/hooks/useLanguage";
import { langList } from "@/i18n";

import { useChangeLanguage } from "./useChangeLanguage";
import { PORTALS } from ".constants/portals";

export const ChangeLangPortal: FC = () => {
  const t = useTranslations("LanguageModal");

  const flag = useFlag();

  const { selectedLanguage, isPending, handleLanguageChange } =
    useChangeLanguage();

  const handleClick = useCallback(
    (event: SyntheticEvent) => {
      const lang = event.currentTarget.getAttribute(
        "data-lang",
      ) as LanguageCode;
      if (lang) {
        handleLanguageChange(lang);
      }
    },
    [handleLanguageChange],
  );

  return (
    <PortalModal portalId={PORTALS.CHANGE_LANG}>
      <ModalHeader>{t("title")}</ModalHeader>

      <ModalContent>
        <Box gap={4} wide>
          {langList.map((lang) => {
            const isSelected = selectedLanguage === lang.key;
            const langCode = lang.key as LanguageCode;

            return (
              <Button
                key={langCode}
                startContent={
                  <Image
                    src={flag(langCode)}
                    width={24}
                    height={24}
                    alt={`${langCode} flag`}
                    priority
                  />
                }
                endContent={isSelected && <Icon icon="done" />}
                spinnerPlacement="end"
                variant={isSelected ? "solid" : "light"}
                color={isSelected ? "secondary" : "regular"}
                centerText
                wide
                data-lang={langCode}
                onClick={handleClick}
                loading={isPending && isSelected}
                disabled={isPending}
              >
                {lang.value}
              </Button>
            );
          })}
        </Box>
      </ModalContent>
    </PortalModal>
  );
};

ChangeLangPortal.displayName = "ChangeLangPortal";
